/**
 * One day, hour by hour (SW-8879): the fleet per hour, then every tractor of
 * the span with its two lanes positioned by time. The roster, the right-now
 * block and the customer cards come from the fleet route (one-day span); each
 * tractor's lanes come from its own day timeline, loaded on its own so one
 * failure is one row's Retry.
 */
import React, { useCallback, useEffect, useState } from "react";
import { styled } from "styled-components";
import { Button } from "../../components/ui/button";
import { fetchTimeline } from "./api";
import ReadinessTimeline from "./ReadinessTimeline";
import TractorsByHour from "./TractorsByHour";
import { LaneLegend } from "./lanes";
import { customerLabel, describeNow, sinceText } from "./labels";
import { FleetResponse, FleetVehicle, Timeline } from "./types";
import { tokens } from "../TractorState/tokens";

interface DayViewProps {
  token?: string;
  day: string;
  fleet: FleetResponse;
  /** Now, when `day` is today; null for a past day. */
  nowIso: string | null;
}

type RowState =
  | { status: "loading" }
  | { status: "ok"; timeline: Timeline }
  | { status: "error"; message: string };

const DEFAULT_TZ = "America/New_York";

const DayView: React.FC<DayViewProps> = ({ token, day, fleet, nowIso }) => {
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const load = useCallback(
    (vehicle: string) => {
      if (!token) return;
      setRows((r) => ({ ...r, [vehicle]: { status: "loading" } }));
      fetchTimeline(token, vehicle, day).then(
        (timeline) =>
          setRows((r) => ({ ...r, [vehicle]: { status: "ok", timeline } })),
        (err) =>
          setRows((r) => ({
            ...r,
            [vehicle]: {
              status: "error",
              message:
                err instanceof Error ? err.message : "The day did not load.",
            },
          })),
      );
    },
    [token, day],
  );

  useEffect(() => {
    setRows({});
    fleet.vehicles.forEach((v) => load(v.vehicle));
  }, [fleet, load]);

  const loaded = fleet.vehicles
    .map((v) => rows[v.vehicle])
    .filter(
      (r): r is { status: "ok"; timeline: Timeline } => r?.status === "ok",
    )
    .map((r) => r.timeline);
  const bounds = loaded.find((t) => t.day_start && t.day_end);

  return (
    <Stack>
      {bounds && bounds.day_start && bounds.day_end && (
        <TractorsByHour
          timelines={loaded}
          dayStart={bounds.day_start}
          dayEnd={bounds.day_end}
          nowIso={nowIso}
        />
      )}
      <LegendLine>
        <LaneLegend />
        <span>
          <MarkerGlyph aria-hidden="true" /> Jira ticket or takeover
        </span>
      </LegendLine>
      {fleet.vehicles.map((v) => (
        <TractorDay
          key={v.vehicle}
          vehicle={v}
          day={day}
          row={rows[v.vehicle] ?? { status: "loading" }}
          nowIso={nowIso}
          onRetry={() => load(v.vehicle)}
        />
      ))}
      {fleet.vehicles.length === 0 && (
        <Muted>No tractors match these filters.</Muted>
      )}
    </Stack>
  );
};

export default DayView;

const TractorDay: React.FC<{
  vehicle: FleetVehicle;
  day: string;
  row: RowState;
  nowIso: string | null;
  onRetry: () => void;
}> = ({ vehicle, day, row, nowIso, onRetry }) => {
  const timeline = row.status === "ok" ? row.timeline : null;
  const tz = timeline?.tz || DEFAULT_TZ;
  const lane = (name: string) =>
    timeline?.lanes.find((l) => l.lane === name)?.intervals ?? [];
  const now = describeNow(vehicle.now);
  const since = vehicle.now ? sinceText(vehicle.now.since) : null;
  const runs =
    vehicle.days.find((d) => d.local_day === day)?.runs ?? vehicle.runs;
  return (
    <Row as="article" aria-label={vehicle.display_name}>
      <Head>
        <Name>{vehicle.display_name}</Name>
        {vehicle.customer && (
          <Customer>{customerLabel(vehicle.customer)}</Customer>
        )}
        {now.tone === "bad" && (
          <Chip $tone="bad">
            Not ready{since ? ` ${since}` : ""}
            {vehicle.now?.reasons.length
              ? ` · ${now.text.replace(/^Not ready · /, "")}`
              : ""}
          </Chip>
        )}
        {now.tone === "muted" && <Chip $tone="muted">No readiness mark</Chip>}
        <Chip $tone="muted">Runs {runs}</Chip>
        <Spacer />
        <StateLink
          href={`/tractor-state/${encodeURIComponent(vehicle.vehicle)}`}
        >
          Update in Tractor State ↗
        </StateLink>
      </Head>
      {row.status === "loading" && <Muted>Loading…</Muted>}
      {row.status === "error" && (
        <Notice role="alert">
          This tractor&apos;s day did not load: {row.message}{" "}
          <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </Notice>
      )}
      {timeline && timeline.day_start && timeline.day_end && (
        <ReadinessTimeline
          dayStart={timeline.day_start}
          dayEnd={timeline.day_end}
          tz={tz}
          readiness={lane("readiness")}
          activity={lane("availability")}
          points={timeline.points}
          name={vehicle.display_name}
          day={day}
          nowIso={nowIso}
          dataThrough={timeline.data_through}
        />
      )}
    </Row>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const LegendLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 16px;
  padding: 0 4px;
  font: 12px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const MarkerGlyph = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid ${tokens.ink};
  margin-right: 6px;
  vertical-align: 1px;
`;

const Row = styled.div`
  background: ${tokens.white};
  border: 1px solid ${tokens.line};
  border-radius: 10px;
  padding: 14px 18px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
`;

const Name = styled.h2`
  margin: 0;
  font: 700 17px ${tokens.fontSans};
  color: ${tokens.navy};
`;

const Customer = styled.span`
  font: 500 10px ${tokens.fontMono};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${tokens.slate};
`;

const Chip = styled.span<{ $tone: "bad" | "muted" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 999px;
  background: ${tokens.offWhite};
  font: 500 11px ${tokens.fontMono};
  color: ${(p) => (p.$tone === "bad" ? tokens.groundedText : tokens.slate)};
  white-space: nowrap;
`;

const Spacer = styled.span`
  flex: 1;
`;

const StateLink = styled.a`
  font: 600 12px ${tokens.fontSans};
  color: ${tokens.blue};
  text-decoration: none;
  white-space: nowrap;
`;

const Muted = styled.p`
  margin: 4px 0 0;
  font: 13px ${tokens.fontSans};
  color: ${tokens.slate};
`;

const Notice = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font: 13px ${tokens.fontSans};
  color: ${tokens.ink};
`;
